/**
 * SignaturePad
 *
 * Full-screen modal presented when the signer taps a SIGNATURE or INITIALS
 * field. Two capture modes:
 *
 *  • Draw  — canvas with pointer/touch events.  Works on desktop and mobile.
 *  • Type  — text input rendered into a canvas using Dancing Script; ideal for
 *            users who don't want to draw on a trackpad.
 *
 * Exports a PNG data URL (transparent background) for the backend to embed
 * into the signed PDF via pdf-lib.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  label: string;
  brandColor: string;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
}

type Mode = 'draw' | 'type';

export default function SignaturePad({ label, brandColor, onConfirm, onCancel }: Props) {
  const [mode, setMode] = useState<Mode>('draw');
  const [typedText, setTypedText] = useState('');
  const [hasContent, setHasContent] = useState(false);

  const drawCanvasRef = useRef<HTMLCanvasElement>(null);
  const typeCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  // ── Draw canvas initialisation ────────────────────────────────────────────

  const initDrawCanvas = useCallback(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    initDrawCanvas();
  }, [initDrawCanvas]);

  const clearDraw = () => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasContent(false);
    lastPoint.current = null;
  };

  // ── Draw pointer events ───────────────────────────────────────────────────

  const canvasCoords = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = drawCanvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onDrawDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    isDrawing.current = true;
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    const pt = canvasCoords(e);
    lastPoint.current = pt;
    // Draw a dot for single taps
    const ctx = drawCanvasRef.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 1.2, 0, Math.PI * 2);
    ctx.fill();
    setHasContent(true);
  };

  const onDrawMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing.current || !lastPoint.current) return;
    const pt = canvasCoords(e);
    const ctx = drawCanvasRef.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPoint.current = pt;
    setHasContent(true);
  };

  const onDrawUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    isDrawing.current = false;
    lastPoint.current = null;
  };

  // ── Type-to-sign preview rendering ───────────────────────────────────────

  const renderTyped = useCallback(async (text: string) => {
    const canvas = typeCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);

    if (!text.trim()) return;
    // Ensure the font is loaded before drawing
    await document.fonts.load(`${rect.height * 0.45}px "Dancing Script"`);
    ctx.font = `${rect.height * 0.45}px "Dancing Script", cursive`;
    ctx.fillStyle = '#1a1a2e';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, rect.width / 2, rect.height / 2, rect.width * 0.88);
  }, []);

  useEffect(() => {
    renderTyped(typedText);
    setHasContent(typedText.trim().length > 0);
  }, [typedText, renderTyped, mode]);

  // Reinitialise canvases when switching modes (layout may have changed)
  useEffect(() => {
    if (mode === 'draw') {
      // Give the DOM a tick to re-layout before measuring
      requestAnimationFrame(() => {
        initDrawCanvas();
        setHasContent(false);
      });
    }
  }, [mode, initDrawCanvas]);

  // ── Confirm: export the active canvas as a PNG data URL ──────────────────

  const handleConfirm = () => {
    const canvas = mode === 'draw' ? drawCanvasRef.current : typeCanvasRef.current;
    if (!canvas) return;
    onConfirm(canvas.toDataURL('image/png'));
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const s = {
    overlay: {
      position: 'fixed' as const,
      inset: 0,
      background: 'rgba(0,0,0,0.6)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    },
    modal: {
      background: '#fff',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '520px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      overflow: 'hidden',
    },
    header: {
      background: brandColor,
      color: '#fff',
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
    },
    tab: (active: boolean): React.CSSProperties => ({
      flex: 1,
      padding: '10px',
      border: 'none',
      background: active ? '#f9fafb' : '#fff',
      borderBottom: active ? `2px solid ${brandColor}` : '2px solid transparent',
      cursor: 'pointer',
      fontWeight: active ? 600 : 400,
      color: active ? brandColor : '#6b7280',
      fontSize: '14px',
      transition: 'all 0.15s',
    }),
    canvasWrap: {
      padding: '20px',
      background: '#f9fafb',
    },
    canvas: {
      display: 'block',
      width: '100%',
      height: '160px',
      background: '#fff',
      border: '1.5px dashed #d1d5db',
      borderRadius: '8px',
      cursor: 'crosshair',
      touchAction: 'none' as const,
    },
    hint: {
      textAlign: 'center' as const,
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '8px',
    },
    typeInput: {
      width: '100%',
      padding: '10px 14px',
      border: '1.5px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      fontFamily: 'inherit',
      marginBottom: '12px',
      boxSizing: 'border-box' as const,
      outline: 'none',
    },
    footer: {
      display: 'flex',
      gap: '10px',
      padding: '16px 20px',
      borderTop: '1px solid #e5e7eb',
      justifyContent: 'space-between',
    },
    btn: (variant: 'primary' | 'secondary' | 'ghost'): React.CSSProperties => ({
      padding: '10px 20px',
      border: variant === 'secondary' ? '1.5px solid #d1d5db' : 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '14px',
      background:
        variant === 'primary' ? brandColor : variant === 'secondary' ? '#fff' : 'transparent',
      color: variant === 'primary' ? '#fff' : variant === 'secondary' ? '#374151' : '#6b7280',
      opacity: variant === 'primary' && !hasContent ? 0.5 : 1,
    }),
  };

  return (
    <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={s.modal}>
        {/* Header */}
        <div style={s.header}>
          <span style={{ fontWeight: 600, fontSize: '16px' }}>{label}</span>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '20px', lineHeight: 1, padding: 0 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Mode tabs */}
        <div style={s.tabs}>
          <button style={s.tab(mode === 'draw')} onClick={() => setMode('draw')}>
            ✍ Draw
          </button>
          <button style={s.tab(mode === 'type')} onClick={() => setMode('type')}>
            Aa Type
          </button>
        </div>

        {/* Canvas area */}
        <div style={s.canvasWrap}>
          {mode === 'draw' ? (
            <>
              <canvas
                ref={drawCanvasRef}
                style={s.canvas}
                onPointerDown={onDrawDown}
                onPointerMove={onDrawMove}
                onPointerUp={onDrawUp}
                onPointerLeave={onDrawUp}
              />
              <p style={s.hint}>Draw your signature above</p>
            </>
          ) : (
            <>
              <input
                type="text"
                placeholder="Type your name"
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                style={s.typeInput}
                autoFocus
              />
              <canvas
                ref={typeCanvasRef}
                style={{ ...s.canvas, cursor: 'default', borderStyle: 'solid' }}
              />
              <p style={s.hint}>Preview of your typed signature</p>
            </>
          )}
        </div>

        {/* Footer actions */}
        <div style={s.footer}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={s.btn('ghost')} onClick={onCancel}>Cancel</button>
            {mode === 'draw' && (
              <button style={s.btn('secondary')} onClick={clearDraw}>Clear</button>
            )}
          </div>
          <button
            style={s.btn('primary')}
            disabled={!hasContent}
            onClick={handleConfirm}
          >
            Confirm signature
          </button>
        </div>
      </div>
    </div>
  );
}
